import { cwd } from 'node:process';
import React from 'react';
import type { ReactElement } from 'react';
import { LoginModeProvider, SiteAuthProvider } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { render } from '@wix/cli-ui-kit';
import { I18nProvider } from '@wix/cli-i18n';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import { BiProvider } from '@wix/cli-telemetry-react';
import type { Flow } from '@wix/cli-telemetry';
import { addUserFields, reportCommandStartEvent } from '@wix/cli-telemetry';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import type { CommandServices } from '@wix/cli-command-contract';
import type { Command } from '@commander-js/extra-typings';
import type { ProjectModel } from '../model.js';
import { ProjectModelProvider, loadProjectModel } from '../model.js';
import messages from '../messages.json' with { type: 'json' };

async function initServices({
  model,
  command,
  cliVersion,
  services,
}: {
  model: ProjectModel;
  command: Command<unknown[]>;
  cliVersion: string;
  services: CommandServices;
}) {
  const { biLogger, errorReporter, panorama } = services;
  const { metaSiteId } = model;

  const project = 'Sites_V2' satisfies Flow;

  errorReporter.setTag('project', project);
  errorReporter.setTag('project.site_id', metaSiteId);

  panorama.setDefaultClientData({ cliProject: project, cliSiteId: metaSiteId });

  biLogger.updateDefaults({
    flow: project,
    command: command.name(),
    cliVersion,
    _msid: metaSiteId,
  });
}

export async function renderCommand(
  command: Command<unknown[]>,
  services: CommandServices,
  cliVersion: string,
  component: ReactElement
) {
  await runCommand(command, services, cliVersion, async (model) => {
    const { biLogger, errorReporter, panorama } = services;

    const handleLogin = (userInfo: UserInfo) => {
      errorReporter.setUser({ id: userInfo.userId });
      addUserFields(biLogger, userInfo.userId);
    };

    const headers = buildHttpClientHeaders({
      flow: 'Sites_V2',
      command: command.name(),
      cliVersion,
    });

    await render(
      <BiProvider value={biLogger}>
        <ErrorReporterProvider value={errorReporter}>
          <PanoramaProvider value={panorama}>
            <LoginModeProvider value={services.loginMode}>
              <SiteAuthProvider siteId={model.metaSiteId} onLogin={handleLogin}>
                <HttpClientProvider headers={headers}>
                  <I18nProvider messages={messages}>
                    <ProjectModelProvider value={model}>
                      {component}
                    </ProjectModelProvider>
                  </I18nProvider>
                </HttpClientProvider>
              </SiteAuthProvider>
            </LoginModeProvider>
          </PanoramaProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
  });
}

async function runCommand(
  command: Command<unknown[]>,
  services: CommandServices,
  cliVersion: string,
  commandFn: (model: ProjectModel) => Promise<void>
) {
  const model = await loadProjectModel(cwd());

  await initServices({ model, services, command, cliVersion });

  const commandStatus = reportCommandStartEvent({
    msid: model.metaSiteId,
    biLogger: services.biLogger,
    command,
    cliVersion,
    flow: 'Sites_V2',
  });

  const transaction = createCommandTransaction(services.panorama, command);

  try {
    transaction.start();
    await commandFn(model);
    transaction.finish();
    commandStatus.succeed();
  } catch (e) {
    commandStatus.failed(e);
    throw e;
  }
}
